/**
 * Light/dark/system theme, stored the same way as the language choice
 * (`localStorage`, no API field — see PLAN-FASE1.md, *Kleurregel*). The
 * inline script in `index.html` sets `data-theme` before first paint using
 * the same rule; this module is what runs after hydration, e.g. from the
 * theme switcher in the account menu.
 */

const STORAGE_KEY = 'theme';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

function prefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** The user's stored choice, or `'system'` if none was made (or storage is unavailable). */
export function getThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // localStorage unavailable (privacy mode, disabled storage, ...) — fall
    // through to the system default.
  }
  return 'system';
}

/** What is actually applied right now: `system` resolved against the OS preference. */
export function getResolvedTheme(): ResolvedTheme {
  const preference = getThemePreference();
  if (preference === 'system') return prefersDark() ? 'dark' : 'light';
  return preference;
}

/**
 * Applies the preference to `<html data-theme>` and persists it.
 * `'system'` clears the stored choice so future OS changes take over again.
 */
export function setTheme(preference: ThemePreference): void {
  try {
    if (preference === 'system') {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, preference);
    }
  } catch {
    // Best-effort persistence; the attribute below still applies for this
    // page load even if storage is unavailable.
  }

  const resolved = preference === 'system' ? (prefersDark() ? 'dark' : 'light') : preference;
  document.documentElement.setAttribute('data-theme', resolved);
}
